import TooltipEllipsis from '@deltek/specification-client/core/Feedbacks/TooltipEllipsis'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    Button,
    FormControl,
    makeStyles,
    MenuItem,
    Popover,
    Theme,
    Typography,
} from '@material-ui/core'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import { useVersionDropdownResizeObserver } from 'components/Workspace2ActionBar/hooks/useVersionDropdownResizeObserver'
import { FamilyIteration } from 'models/projectElementNode'
import React, { useCallback, useMemo, useState } from 'react'

// Constants
const DROPDOWN_CONFIG = {
    maxHeight: pxToRem(225),
    maxWidth: pxToRem(259),
    padding: 30,
    textOffset: 65,
    borderRadius: '4px',
} as const

const TEXT_CONFIG = {
    emptyText: 'Current',
    noOptionsText: 'No Options Available',
    sectionPrefix: 'Section',
    iterationSeparator: '- ',
} as const

const useStyles = makeStyles((theme: Theme) => ({
    menuItems: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: 'block',
        fontFamily: 'Roboto',
        fontWeight: 400,
        fontStyle: 'Regular',
        fontSize: pxToRem(14),
        lineHeight: '30px',
        color: '#424242',
        '&.MuiMenuItem-gutters': {
            paddingLeft: pxToRem(5),
            paddingRight: pxToRem(5),
        },
    },
    triggerButton: {
        height: pxToRem(23),
        width: '100%',
        maxWidth: DROPDOWN_CONFIG.maxWidth,
        border: '1px solid #CCCCCC',
        borderRadius: pxToRem(100),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 8px',
        backgroundColor: 'white',
        '&:hover': {
            backgroundColor: '#f5f5f5',
        },
    },
    selectedValues: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    versionLabel: {
        fontFamily: 'Roboto',
        fontWeight: 400,
        fontSize: pxToRem(13),
        color: '#314959',
        textTransform: 'capitalize',
        lineHeight: '100%',
        marginRight: pxToRem(8),
    },
    versionText: {
        fontFamily: 'Roboto',
        fontWeight: 500,
        fontSize: pxToRem(11),
        lineHeight: '100%',
        textTransform: 'uppercase',
        color: '#BB5900',
    },
    popoverContent: {
        maxHeight: DROPDOWN_CONFIG.maxHeight,
        overflowY: 'auto',
        opacity: 1,
        borderRadius: DROPDOWN_CONFIG.borderRadius,
        padding: '10px',
    },
    dropdownArrow: {
        '&.MuiSvgIcon-root': { color: '#314959' },
        height: pxToRem(10),
        width: 'auto',
    },
    versionDropdownContainer: {
        width: '100%',
        maxWidth: DROPDOWN_CONFIG.maxWidth,
    },
}))

interface VersionDropdownProps {
    options: FamilyIteration[] | null | undefined
    handleChange: (value: FamilyIteration) => void
    value: FamilyIteration | undefined
    displayEmpty?: boolean
    isOM?: boolean
}

// Custom hook for menu item label generation
const useMenuItemLabel = () => {
    return useCallback((option: FamilyIteration | null | undefined) => {
        if (!option) return ''

        const familyNumber = option.familyNumber
        const iterationName = option.iterationName
        const iterationPart = iterationName
            ? `${TEXT_CONFIG.iterationSeparator}${iterationName}`
            : ''

        return `${TEXT_CONFIG.sectionPrefix} ${familyNumber} ${iterationPart}`.trim()
    }, [])
}

// Custom hook for dropdown value management
const useDropdownValue = (
    value: FamilyIteration | undefined,
    displayEmpty: boolean,
    getMenuItemLabel: (option: FamilyIteration | null | undefined) => string
) => {
    return useMemo(() => {
        if (!value) {
            return displayEmpty ? TEXT_CONFIG.emptyText : ''
        }

        if (Array.isArray(value)) {
            return value.map(getMenuItemLabel).join(', ')
        }

        return getMenuItemLabel(value)
    }, [value, displayEmpty, getMenuItemLabel])
}

// Custom hook for option mapping
const useOptionMap = (
    options: FamilyIteration[] | null | undefined,
    getMenuItemLabel: (option: FamilyIteration | null | undefined) => string
) => {
    return useMemo(() => {
        const map = new Map<string, FamilyIteration>()

        if (!options?.length) return map

        for (const option of options) {
            if (option) {
                const label = getMenuItemLabel(option)
                if (label) {
                    map.set(label, option)
                }
            }
        }

        return map
    }, [options, getMenuItemLabel])
}

// Custom hook for selection handlers
const useSelectionHandlers = (
    value: FamilyIteration | undefined,
    optionMap: Map<string, FamilyIteration>,
    handleChange: (value: FamilyIteration) => void,
    setAnchorEl: (el: HTMLElement | null) => void,
    getMenuItemLabel: (option: FamilyIteration | null | undefined) => string
) => {
    const handleMenuItemClick = useCallback(
        (label: string) => {
            const selectedOption = optionMap.get(label)
            if (selectedOption) {
                handleChange(selectedOption)
            }
            setAnchorEl(null)
        },
        [optionMap, handleChange, setAnchorEl]
    )

    const isSelected = useCallback(
        (label: string) => {
            if (!value) return false

            if (Array.isArray(value)) {
                return value.some((v) => getMenuItemLabel(v) === label)
            }

            return getMenuItemLabel(value) === label
        },
        [value, getMenuItemLabel]
    )

    return { handleMenuItemClick, isSelected }
}

const VersionDropdown: React.FC<VersionDropdownProps> = ({
    options,
    handleChange,
    value,
    displayEmpty = true,
    isOM = true,
}) => {
    const classes = useStyles()
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const { versionDropdownRef, textWidth } = useVersionDropdownResizeObserver()

    // Custom hooks
    const getMenuItemLabel = useMenuItemLabel()
    const dropdownValue = useDropdownValue(
        value,
        displayEmpty,
        getMenuItemLabel
    )
    const optionMap = useOptionMap(options, getMenuItemLabel)
    const { handleMenuItemClick, isSelected } = useSelectionHandlers(
        value,
        optionMap,
        handleChange,
        setAnchorEl,
        getMenuItemLabel
    )

    // Event handlers
    const handleTriggerClick = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            setAnchorEl(event.currentTarget)
        },
        []
    )

    const handleClose = useCallback(() => {
        setAnchorEl(null)
    }, [])

    // Computed values
    const popoverWidth = useMemo(() => textWidth + DROPDOWN_CONFIG.padding, [
        textWidth,
    ])
    const tooltipWidth = useMemo(
        () => Math.max(0, textWidth - DROPDOWN_CONFIG.textOffset),
        [textWidth]
    )
    const isOpen = Boolean(anchorEl)
    const hasOptions = optionMap.size > 0
    const versionTypeLabel = isOM ? 'Version' : 'MasterSpec'

    return (
        <FormControl
            data-testid="versionDropdown"
            variant="outlined"
            className={classes.versionDropdownContainer}
            ref={versionDropdownRef}
        >
            <Button
                data-testid="versionSelect"
                className={classes.triggerButton}
                onClick={handleTriggerClick}
                disableRipple
                aria-expanded={isOpen}
                aria-haspopup="listbox"
            >
                <div className={classes.selectedValues}>
                    <Typography className={classes.versionLabel}>
                        {versionTypeLabel}
                    </Typography>
                    <Typography className={classes.versionText}>
                        <TooltipEllipsis
                            text={dropdownValue}
                            width={tooltipWidth}
                        >
                            {dropdownValue}
                        </TooltipEllipsis>
                    </Typography>
                </div>
                <KeyboardArrowDownIcon
                    fontSize="inherit"
                    className={classes.dropdownArrow}
                    aria-hidden="true"
                />
            </Button>

            <Popover
                open={isOpen}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    role: 'listbox',
                    'aria-label': `${versionTypeLabel} options`,
                }}
            >
                <div
                    className={classes.popoverContent}
                    style={{ width: popoverWidth }}
                >
                    {hasOptions ? (
                        Array.from(optionMap.entries()).map(
                            ([label, option]) => {
                                const menuItemLabel = getMenuItemLabel(option)
                                const selected = isSelected(label)

                                return (
                                    <MenuItem
                                        key={label}
                                        className={classes.menuItems}
                                        onClick={() =>
                                            handleMenuItemClick(label)
                                        }
                                        selected={selected}
                                        role="option"
                                        aria-selected={selected}
                                    >
                                        <TooltipEllipsis
                                            text={menuItemLabel}
                                            width={textWidth}
                                        >
                                            {menuItemLabel}
                                        </TooltipEllipsis>
                                    </MenuItem>
                                )
                            }
                        )
                    ) : (
                        <MenuItem disabled role="option">
                            {TEXT_CONFIG.noOptionsText}
                        </MenuItem>
                    )}
                </div>
            </Popover>
        </FormControl>
    )
}

export default React.memo(VersionDropdown)
